/**
 * Convert record citation tags into custom HTML tags rendered by ReactMarkdown.
 *
 * Syntax emitted by the LLM:
 *   [[record:MEMBER_ID:DOC_ID:CHUNK_INDEX]]
 *
 * The older spike syntax [[record:DOC_ID:CHUNK_INDEX]] is still supported
 * so previously generated messages continue to render.
 */

const RECORD_REF_WITH_MEMBER =
  /^\[\[record:((?:[A-Z]{2}-\d{3})|\d+):([A-Za-z0-9_.-]+):(\d+)]]$/;
const RECORD_REF_LEGACY = /^\[\[record:([A-Za-z0-9_.-]+):(\d+)]]$/;
const DOC_ID_PATTERN = /^[A-Za-z0-9_.-]+$/;

/**
 * Parse a full [[record:...]] citation ref into { memberId?, docId, chunkIndex }.
 * Returns null when the string is not a supported citation tag.
 */
export function parseRecordCitationRef(ref) {
  if (!ref || typeof ref !== 'string') return null;
  const trimmed = ref.trim();
  const withMember = trimmed.match(RECORD_REF_WITH_MEMBER);
  if (withMember) {
    return {
      memberId: withMember[1],
      docId: withMember[2],
      chunkIndex: withMember[3],
    };
  }
  const legacy = trimmed.match(RECORD_REF_LEGACY);
  if (legacy) {
    return {
      docId: legacy[1],
      chunkIndex: legacy[2],
    };
  }
  return null;
}

/**
 * Normalize a bare document id / path into a vector doc id.
 * Accepts hex ids and optional `.json` suffix; rejects citation tags / paths.
 */
export function parseRecordDocId(pathOrId) {
  if (!pathOrId || typeof pathOrId !== 'string') return null;
  let value = pathOrId.trim();
  if (!value || value.startsWith('[[')) return null;
  if (value.includes('/')) {
    value = value.split('/').pop() || '';
  }
  if (value.toLowerCase().endsWith('.json')) {
    value = value.slice(0, -5);
  }
  if (!value || !DOC_ID_PATTERN.test(value)) return null;
  return value;
}

export function preprocessRecordCitations(text) {
  if (!text) return text;
  const withMemberId = text.replace(
    /\[\[record:((?:[A-Z]{2}-\d{3})|\d+):([A-Za-z0-9_.-]+):(\d+)]]/g,
    (_, memberId, docId, chunkIndex) => (
      `<record-citation data-member-id="${memberId}" data-doc-id="${docId}" data-chunk-index="${chunkIndex}"></record-citation>`
    )
  );
  return withMemberId.replace(
    /\[\[record:([A-Za-z0-9_.-]+):(\d+)]]/g,
    (_, docId, chunkIndex) => (
      `<record-citation data-doc-id="${docId}" data-chunk-index="${chunkIndex}"></record-citation>`
    )
  );
}

export function stripRecordCitations(text) {
  if (!text) return text;
  return text
    .replace(/\s*\[\[record:(?:(?:[A-Z]{2}-\d{3})|\d+):[A-Za-z0-9_.-]+:\d+]]/g, '')
    .replace(/\s*\[\[record:[A-Za-z0-9_.-]+:\d+]]/g, '');
}

export function preprocessChatSyntax(text) {
  return preprocessRecordCitations(text);
}
